import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import HeroMetricCard from '../../pages/protected/deal/dashboard/HeroMetricCard';
import MetricCard from '../../pages/protected/deal/dashboard/MetricCard';
import { focusRing } from './landingStyles';
import { useCountUp } from './investMotion';

const SAMPLE_COMPS = [
  { name: 'Sample comp 1', price: 240000 },
  { name: 'Sample comp 2', price: 248000 },
  { name: 'Sample comp 3', price: 250000 },
];

const CASH_ON_CASH = `${((48500 / 62000) * 100).toFixed(1)}%`;

const EXAMPLE_SUMMARY = [
  'DealSkout illustrative example — not a live analysis and not a recommendation to purchase.',
  '123 Example Street',
  'Modeled Deal Grade A (illustrative label, not an engine grade for this sample)',
  'Projected net profit: $48,500',
  'Cash required: $62,000',
  `Cash-on-cash return: ${CASH_ON_CASH} (projected net profit divided by cash required, not annualized)`,
  'ARV: $250,000 (illustrative example)',
  'Comp indication: $246,000 (illustrative example)',
  'Sample comps are an illustrative split of that $246,000 indication: Sample comp 1 $240,000, Sample comp 2 $248,000, Sample comp 3 $250,000. Not a comp search and not a listing.',
  'Purchase price and rehab are inputs you enter on a saved deal. This sample does not invent those dollars.',
].join('\n');

/**
 * Fixed illustrative figures. These components do not load a deal or call an API.
 * The three sample comps average to the published $246,000 indication.
 */
const ProductPreview: React.FC = () => {
  const theme = useTheme();
  const [copyState, setCopyState] = React.useState('');
  const profit = useCountUp(48500);
  const cash = useCountUp(62000);
  const arv = useCountUp(250000);
  const indication = useCountUp(246000);
  const compValues = [useCountUp(240000), useCountUp(248000), useCountUp(250000)];

  const copySummary = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard');
      await navigator.clipboard.writeText(EXAMPLE_SUMMARY);
      setCopyState('Copied. The summary still says this is an example.');
    } catch {
      setCopyState('Copy is unavailable in this browser. The figures above are an illustrative example.');
    }
  };

  return (
    <Box
      component="section"
      aria-labelledby="preview-title"
      className="ds-report"
      sx={{
        width: '100%',
        mb: { xs: 6, md: 8 },
        minWidth: 0,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 3,
        '--ds-accent': theme.palette.brand.yellow,
        background: (t) =>
          `linear-gradient(${t.palette.background.paper}, ${t.palette.background.paper}) padding-box, linear-gradient(145deg, ${t.palette.brand.yellow}, ${t.palette.brand.navy}) border-box`,
        border: '1px solid transparent',
        boxShadow: (t) =>
          `0 18px 42px color-mix(in srgb, ${t.palette.brand.yellow} 24%, transparent)`,
      }}
    >
      <Box className="ds-report-shine" aria-hidden="true" />
      <Box
        sx={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'minmax(240px, 0.72fr) minmax(0, 1.28fr)' },
          gap: { xs: 2.5, md: 3 },
          p: { xs: 2, md: 3 },
          alignItems: 'start',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 0.5, minWidth: 0 }}>
            <Typography
              id="preview-title"
              component="h2"
              variant="h6"
              sx={{ fontWeight: 800, mr: 0.5, minWidth: 0, overflowWrap: 'break-word', wordBreak: 'normal' }}
            >
              123 Example Street
            </Typography>
            <Chip size="small" color="success" label="Modeled Deal Grade A" />
            <Chip size="small" variant="outlined" label="Illustrative example" />
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Sample figures, not a live analysis and not a recommendation to purchase.
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Source: illustrative example written into this page. Not a saved deal, not a comp search, and not a listing.
          </Typography>
          <Box
            sx={{
              mb: 1.5,
              p: 1.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderLeft: '4px solid',
              borderLeftColor: 'brand.yellow',
              bgcolor: 'background.default',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Sample verdict
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Modeled Deal Grade A is the label on this sample. On a saved deal, DealSkout grades A–F from the
              numbers you enter. This grade is illustrative and is not a recommendation to purchase.
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Purchase price and rehab are inputs you enter on a saved deal. This sample does not invent those dollars.
            The cash-on-cash figure is projected net profit divided by cash required, and it is not annualized.
          </Typography>
          <Button size="small" variant="outlined" onClick={copySummary} sx={{ ...focusRing }}>
            Copy example summary
          </Button>
          {copyState && (
            <Typography variant="caption" role="status" display="block" sx={{ mt: 1.5 }}>
              {copyState}
            </Typography>
          )}
        </Box>

        <Box sx={{ minWidth: 0, display: 'grid', gap: 1.5 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
              gap: 1.5,
            }}
          >
            <Box className="ds-stat">
              <HeroMetricCard label="Projected net profit" value={profit} />
            </Box>
            <Box className="ds-stat">
              <HeroMetricCard label="Cash required" value={cash} />
            </Box>
            <Box className="ds-stat">
              <HeroMetricCard
                label="Cash-on-cash return"
                value={CASH_ON_CASH}
                help="Projected net profit divided by cash required for this sample. Not annualized."
              />
            </Box>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            <Box className="ds-stat">
              <MetricCard label="ARV" value={arv} footnote="Illustrative example" />
            </Box>
            <Box className="ds-stat">
              <MetricCard label="Comp indication" value={indication} footnote="Not a recommendation to purchase" />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.15fr) minmax(180px, 0.85fr)' },
              gap: 2,
              p: { xs: 1.5, md: 2 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default',
              minWidth: 0,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                Sample comps
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Illustrative split of the $246,000 indication. Not a comp search and not a listing.
              </Typography>
              <Box
                component="table"
                sx={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  '& th, & td': {
                    textAlign: 'left',
                    py: 0.75,
                    pr: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    verticalAlign: 'baseline',
                  },
                }}
              >
                <thead>
                  <tr>
                    <Typography component="th" variant="caption" color="text.secondary" scope="col">
                      Sample
                    </Typography>
                    <Typography component="th" variant="caption" color="text.secondary" scope="col">
                      Sale price
                    </Typography>
                  </tr>
                </thead>
                <tbody>
                  {SAMPLE_COMPS.map((comp, index) => (
                    <tr key={comp.name} className="ds-row">
                      <Typography component="td" variant="body2">
                        {comp.name}
                      </Typography>
                      <Typography component="td" variant="body2" sx={{ fontWeight: 700 }}>
                        {compValues[index]}
                      </Typography>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Box>
            <Box aria-hidden="true" sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Illustrative split
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.25, height: 128 }}>
                {SAMPLE_COMPS.map((comp, index) => (
                  <Box key={comp.name} sx={{ flex: 1, minWidth: 0, height: '100%', display: 'flex', alignItems: 'flex-end' }}>
                    <Box
                      className="ds-bar"
                      sx={{
                        width: '100%',
                        height: `${(comp.price / 250000) * 100}%`,
                        borderRadius: '6px 6px 2px 2px',
                        bgcolor: 'brand.yellow',
                        opacity: 0.9,
                        animationDelay: `${index * 0.1}s`,
                      }}
                    />
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: 'flex', gap: 1.25, mt: 0.75 }}>
                {SAMPLE_COMPS.map((comp, index) => (
                  <Typography key={comp.name} variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 0 }}>
                    {index + 1}
                  </Typography>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductPreview;
